import { useTranslation } from 'react-i18next';
import * as actions from '@actions';
import { useDispatch } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { Select } from 'antd';

import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import { SessionFormItem } from '@components/Clinicians/Session/Components/SessionFormItem';
import { useSessionAction } from '@hooks/useSessionAction';
import { ApptActionCodes } from 'vl-common/src/schemas/getMetaAppointments';

type SubspecialityFormItemT = {
  actionCode: ApptActionCodes;
  disabled?: boolean;
  onChange?: (value: string) => void;
};

export const SubspecialityFormItem = ({ actionCode, disabled, onChange }: SubspecialityFormItemT) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const appointment_id = useSessionAppointmentId();

  const { SPECIALITY_KEY, SUBSPECIALITY_KEY } = useSessionAction(actionCode);
  const {
    appointment: { appointment_completed: isCompleted, read_only: readonly }
  } = useSessionAppointment();

  const { detailsCurrentAppointment } = useAppointmentDetails(appointment_id);
  const { action_spec_area_code } =
    detailsCurrentAppointment?.appt_actions.find(({ appt_action_code }) => appt_action_code === actionCode) || {};

  const [speciality] = useSessionFormState(SPECIALITY_KEY);
  const [subSpeciality, setSubSpeciality] = useSessionFormState(SUBSPECIALITY_KEY, action_spec_area_code || undefined);

  const { data: subSpecialitiesMeta } = useQuery({
    queryKey: ['subSpecialitiesMeta', speciality],
    queryFn: () => dispatch(actions.getSubSpecialities(String(speciality), 1)),
    select: (data) => {
      if (!speciality) return [];
      const subSpecs =
        data?.areas ??
        (
          data as any as {
            [key: string]: {
              enabled: boolean;
              category_code: string;
              category_name: string;
              area_code: string;
              area_name: string;
              image_url: string | null;
              no_users: number;
              cat_no_users: number;
            }[];
          }
        )?.[speciality];
      return Number(subSpecs?.length) > 1 ? subSpecs : [];
    },
    enabled: !!speciality,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  const disableSubSpeciality = Number(subSpecialitiesMeta?.length) === 0;

  return (
    <SessionFormItem
      name={SUBSPECIALITY_KEY}
      label={t('Which subspecialty are you referring to?')}
      required={!disableSubSpeciality}
    >
      <Select
        showSearch
        disabled={isCompleted || readonly || !speciality || disableSubSpeciality || disabled}
        data-testid="subspeciality-select"
        value={subSpeciality}
        onSelect={(value) => {
          setSubSpeciality(value);

          onChange?.(value);
        }}
        options={subSpecialitiesMeta?.map((res) => ({
          label: res.area_name,
          value: res.area_code
        }))}
      />
    </SessionFormItem>
  );
};
